<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, Input, useToast } from '@manta/ui-kit';
import {
  ArrowLeft,
  Check,
  Loader2,
  Mail,
  PawPrint,
  Pencil,
  Phone,
  Plus,
  X,
} from 'lucide-vue-next';
import ClientAvatar from './ClientAvatar.vue';
import IntegrationBadge from './IntegrationBadge.vue';
import SpeciesSelect from '@/components/SpeciesSelect.vue';
import { usePatientCreate } from '@/api/patients/usePatientCreate';
import { usePatientUpdate } from '@/api/patients/usePatientUpdate';
import {
  clientDisplayName,
  isClientEditable,
  isPatientEditable,
} from '@/lib/clients';
import { speciesKeyOf, type SpeciesKey } from '@/lib/species';
import type { MantaClient, MantaPatient } from '@/types';

const props = defineProps<{
  client: MantaClient;
  animals: MantaPatient[];
  selectedIds: Set<string>;
  animalsLoading: boolean;
}>();

const emit = defineEmits<{
  back: [];
  edit: [];
  toggle: [id: string];
  confirm: [];
  animalCreated: [animal: MantaPatient];
  animalUpdated: [animal: MantaPatient];
}>();

const { t } = useI18n();
const { toast } = useToast();
const createPatient = usePatientCreate();
const updatePatient = usePatientUpdate();

const name = computed(() => clientDisplayName(props.client));
const editable = computed(() => isClientEditable(props.client));
const selectedCount = computed(
  () => props.animals.filter((a) => props.selectedIds.has(a.id)).length,
);

const adding = ref(false);
const newName = ref('');
const newSpecies = ref<SpeciesKey | ''>('');

const editingId = ref<string | null>(null);
const editName = ref('');
const editSpecies = ref<SpeciesKey | ''>('');

function speciesLabel(animal: MantaPatient): string {
  const key = speciesKeyOf(animal.species);
  return key ? t(`workspace.species.${key}`) : (animal.species ?? '');
}

function animalMeta(animal: MantaPatient): string {
  return [speciesLabel(animal), animal.breed].filter(Boolean).join(' · ');
}

function focusIn(selector: string) {
  void nextTick(() => {
    document.querySelector<HTMLInputElement>(selector)?.focus();
  });
}

function startAdd() {
  editingId.value = null;
  adding.value = true;
  focusIn('[data-client-picker-add-animal]');
}

function cancelAdd() {
  adding.value = false;
  newName.value = '';
  newSpecies.value = '';
}

async function submitAdd() {
  const animalName = newName.value.trim();
  if (!animalName || createPatient.isPending.value) return;
  try {
    const created = await createPatient.mutateAsync({
      name: animalName,
      species: newSpecies.value || undefined,
      client_id: props.client.id,
    });
    emit('animalCreated', created);
    newName.value = '';
    newSpecies.value = '';
    focusIn('[data-client-picker-add-animal]');
  } catch {
    toast({ title: t('workspace.addAnimalError'), variant: 'destructive' });
  }
}

function startEdit(animal: MantaPatient) {
  adding.value = false;
  editingId.value = animal.id;
  editName.value = animal.name ?? '';
  editSpecies.value = speciesKeyOf(animal.species) ?? '';
  focusIn('[data-client-picker-edit-animal]');
}

function cancelEdit() {
  editingId.value = null;
}

async function submitEdit(animal: MantaPatient) {
  const animalName = editName.value.trim();
  if (!animalName || updatePatient.isPending.value) return;
  const previousKey = speciesKeyOf(animal.species) ?? '';
  const data = {
    ...(animalName !== (animal.name ?? '') ? { name: animalName } : {}),
    ...(editSpecies.value !== previousKey
      ? { species: editSpecies.value || null }
      : {}),
  };
  if (!Object.keys(data).length) {
    cancelEdit();
    return;
  }
  try {
    const updated = await updatePatient.mutateAsync({ id: animal.id, data });
    emit('animalUpdated', updated);
    cancelEdit();
  } catch {
    toast({ title: t('workspace.saveAnimalError'), variant: 'destructive' });
  }
}
</script>

<template>
  <div class="flex items-center gap-1 border-b px-2 py-1.5">
    <button
      type="button"
      class="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
      :aria-label="t('workspace.changeCustomer')"
      :title="t('workspace.changeCustomer')"
      @click="emit('back')"
    >
      <ArrowLeft class="size-4" />
    </button>
    <span class="text-sm text-muted-foreground">{{
      t('workspace.customerLabel')
    }}</span>
  </div>

  <div class="flex items-center gap-3 px-3 py-3">
    <ClientAvatar :name="name" size="lg" />
    <div class="min-w-0 flex-1">
      <div class="truncate font-semibold">
        {{ name || t('workspace.unnamedCustomer') }}
      </div>
      <div
        class="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground"
      >
        <span v-if="client.phone_number" class="flex items-center gap-1">
          <Phone class="size-3" />{{ client.phone_number }}
        </span>
        <span v-if="client.email" class="flex min-w-0 items-center gap-1">
          <Mail class="size-3 shrink-0" />
          <span class="truncate">{{ client.email }}</span>
        </span>
        <span v-if="!client.phone_number && !client.email">
          {{ t('workspace.noContactDetails') }}
        </span>
      </div>
    </div>
    <Button
      v-if="editable"
      variant="outline"
      size="sm"
      class="h-8 shrink-0 gap-1.5"
      @click="emit('edit')"
    >
      <Pencil class="size-3.5" />
      {{ t('workspace.edit') }}
    </Button>
    <IntegrationBadge
      v-else-if="client.managed_by"
      :sub-type="client.integration?.sub_type"
      variant="pill"
    />
  </div>

  <div class="flex items-center justify-between border-t px-3 pb-1 pt-2.5">
    <span class="text-xs font-medium text-muted-foreground">
      {{ t('workspace.animalsLabel') }}
    </span>
    <span v-if="animals.length" class="text-xs text-muted-foreground">
      {{ t('workspace.animalsSelected', { n: selectedCount }) }}
    </span>
  </div>

  <div class="max-h-[260px] overflow-y-auto px-1.5 pb-1.5">
    <div
      v-if="animalsLoading && !animals.length"
      class="flex justify-center py-4"
    >
      <Loader2 class="size-4 animate-spin text-muted-foreground" />
    </div>

    <template v-for="animal in animals" :key="animal.id">
      <form
        v-if="editingId === animal.id"
        class="flex items-center gap-1 rounded-lg bg-accent/60 p-1.5"
        @submit.prevent="submitEdit(animal)"
        @keydown.esc.stop.prevent="cancelEdit"
      >
        <Input
          v-model="editName"
          data-client-picker-edit-animal
          class="h-8 min-w-0 flex-1 bg-background"
          :placeholder="t('workspace.patientForm.name')"
        />
        <SpeciesSelect v-model="editSpecies" />
        <Button
          type="submit"
          size="icon"
          class="size-8 shrink-0"
          :disabled="!editName.trim() || updatePatient.isPending.value"
          :aria-label="t('common.save')"
        >
          <Loader2
            v-if="updatePatient.isPending.value"
            class="size-3.5 animate-spin"
          />
          <Check v-else class="size-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          class="size-8 shrink-0"
          :aria-label="t('common.cancel')"
          @click="cancelEdit"
        >
          <X class="size-3.5" />
        </Button>
      </form>

      <div
        v-else
        role="checkbox"
        tabindex="0"
        :aria-checked="selectedIds.has(animal.id)"
        class="group flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 outline-none transition-colors hover:bg-accent focus-visible:bg-accent"
        @click="emit('toggle', animal.id)"
        @keydown.space.prevent="emit('toggle', animal.id)"
        @keydown.enter.prevent="emit('toggle', animal.id)"
      >
        <span
          class="flex size-4 shrink-0 items-center justify-center rounded border transition-colors"
          :class="
            selectedIds.has(animal.id)
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-muted-foreground/40'
          "
        >
          <Check v-if="selectedIds.has(animal.id)" class="size-3" />
        </span>
        <PawPrint class="size-4 shrink-0 text-muted-foreground" />
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm">{{ animal.name || '—' }}</span>
          <span
            v-if="animalMeta(animal)"
            class="block truncate text-xs text-muted-foreground"
          >
            {{ animalMeta(animal) }}
          </span>
        </span>
        <button
          v-if="isPatientEditable(animal)"
          type="button"
          class="shrink-0 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-background hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
          :aria-label="t('workspace.editAnimal')"
          :title="t('workspace.editAnimal')"
          @click.stop="startEdit(animal)"
        >
          <Pencil class="size-3.5" />
        </button>
      </div>
    </template>

    <p
      v-if="!animals.length && !animalsLoading && !adding"
      class="px-2 py-2 text-sm text-muted-foreground"
    >
      {{ t('workspace.noAnimals') }}
    </p>

    <form
      v-if="adding"
      class="mt-1 flex items-center gap-1 rounded-lg bg-accent/60 p-1.5"
      @submit.prevent="submitAdd"
      @keydown.esc.stop.prevent="cancelAdd"
    >
      <Input
        v-model="newName"
        data-client-picker-add-animal
        class="h-8 min-w-0 flex-1 bg-background"
        :placeholder="t('workspace.patientForm.name')"
      />
      <SpeciesSelect v-model="newSpecies" />
      <Button
        type="submit"
        size="icon"
        class="size-8 shrink-0"
        :disabled="!newName.trim() || createPatient.isPending.value"
        :aria-label="t('workspace.addAnimal')"
      >
        <Loader2
          v-if="createPatient.isPending.value"
          class="size-3.5 animate-spin"
        />
        <Check v-else class="size-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        class="size-8 shrink-0"
        :aria-label="t('common.cancel')"
        @click="cancelAdd"
      >
        <X class="size-3.5" />
      </Button>
    </form>
    <button
      v-else
      type="button"
      class="mt-1 flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      @click="startAdd"
    >
      <Plus class="size-4" />
      {{ t('workspace.addAnimal') }}
    </button>
  </div>

  <div class="border-t p-2">
    <Button class="w-full" size="sm" data-autofocus @click="emit('confirm')">
      {{ t('workspace.useCustomerWithAnimals', selectedCount) }}
    </Button>
  </div>
</template>
